/**
 * The language registry — codes, endonyms and writing direction. Deliberately separate from
 * the dictionaries: this list is needed on every page (the switcher renders it), while the
 * dictionaries are not. Keeping them apart is what lets the client bundle stay the same size
 * whether the site ships 2 languages or 24.
 *
 * Adding a language is two edits:
 *   1. drop src/i18n/locales/<code>.json next to the others;
 *   2. add its row here.
 * The pre-render asserts the two sides match, so a half-done addition fails the build instead
 * of shipping a switcher entry that 404s.
 *
 * A language is named in its own language and never translated. The order below is the owner's,
 * not alphabetical, and it is the order the switcher renders — the list is the single source of
 * that ordering, so the vertical menu never has to sort anything itself.
 *
 * `dir` is what reaches <html dir> in every pre-rendered page (scripts/prerender.mjs), so a
 * right-to-left language needs its row here and nothing else. Arabic is the only rtl entry:
 * Persian and Hebrew are not in the set, and Urdu is not either.
 */
export interface Language {
  code: string;
  /** The language's name in itself. */
  endonym: string;
  dir: "ltr" | "rtl";
}

export const LANGUAGES = [
  { code: "en", endonym: "English", dir: "ltr" },
  { code: "zh-Hans", endonym: "中文 (简体)", dir: "ltr" },
  { code: "ru", endonym: "Русский", dir: "ltr" },
  { code: "es", endonym: "Español", dir: "ltr" },
  { code: "pt-BR", endonym: "Português (Brasil)", dir: "ltr" },
  { code: "tr", endonym: "Türkçe", dir: "ltr" },
  { code: "vi", endonym: "Tiếng Việt", dir: "ltr" },
  { code: "id", endonym: "Bahasa Indonesia", dir: "ltr" },
  { code: "hi", endonym: "हिन्दी", dir: "ltr" },
  { code: "ar", endonym: "العربية", dir: "rtl" },
  { code: "fil", endonym: "Filipino", dir: "ltr" },
  { code: "th", endonym: "ไทย", dir: "ltr" },
  { code: "uk", endonym: "Українська", dir: "ltr" },
  { code: "bn", endonym: "বাংলা", dir: "ltr" },
  { code: "kk", endonym: "Қазақша", dir: "ltr" },
  { code: "sw", endonym: "Kiswahili", dir: "ltr" },
  { code: "ko", endonym: "한국어", dir: "ltr" },
  { code: "ja", endonym: "日本語", dir: "ltr" },
  { code: "zh-Hant", endonym: "中文 (繁體)", dir: "ltr" },
  { code: "yue", endonym: "廣東話", dir: "ltr" },
  { code: "de", endonym: "Deutsch", dir: "ltr" },
  { code: "pl", endonym: "Polski", dir: "ltr" },
  { code: "fr", endonym: "Français", dir: "ltr" },
  { code: "it", endonym: "Italiano", dir: "ltr" },
] as const satisfies readonly Language[];

export type Locale = (typeof LANGUAGES)[number]["code"];

export const LOCALES: readonly Locale[] = LANGUAGES.map((language) => language.code);

/** Used for "/" when Accept-Language matches nothing, and as the hreflang x-default. */
export const DEFAULT_LOCALE: Locale = "en";

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function languageOf(locale: Locale): Language {
  const found = LANGUAGES.find((language) => language.code === locale);
  if (!found) throw new Error(`${locale} is not in the language registry`);
  return found;
}

/** A year. The choice outlives the visit, which is the whole point of storing it. */
const REMEMBER_SECONDS = 365 * 24 * 60 * 60;

/**
 * Stores the visitor's language so the next visit to "/" lands on it.
 *
 * A cookie, not localStorage: the redirect for "/" happens on the host
 * (Localization/LocaleNegotiation.cs), before any script runs, and the host can only read a
 * cookie. Storing it anywhere else would look like it worked and then send the visitor back to
 * their browser's language on the next load.
 *
 * The value is the canonical code from the registry — the same spelling as the pre-rendered
 * directory — because the host matches it against exactly that set and silently ignores
 * anything else. Codes are ASCII letters and hyphens, so nothing here needs escaping.
 *
 * This is the only cookie the site sets, which is what the Privacy Policy says and why there is
 * no consent banner: it is strictly necessary, carries no identifier and is never read by
 * anything but the locale redirect. Do not add a second one here.
 */
export function rememberLocale(locale: Locale): void {
  // The pre-render calls this module too; there is no document there and nothing to remember.
  if (typeof document === "undefined") return;

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `lang=${locale}; Max-Age=${REMEMBER_SECONDS}; Path=/; SameSite=Lax${secure}`;
}
